import React, { useMemo } from 'react'
import { t } from '../../i18n/i18n'
import { Dropdown, Field, Option, OptionGroup, makeStyles, tokens } from '@fluentui/react-components'
import { Control, Controller } from 'react-hook-form'
import { OrganisationWithUserAccountsDTO, SNI, SNIOrganisationConnection } from '../../api/schemas/schema'

const useStyles = makeStyles({
   optionGroupHeader: {
      fontWeight: tokens.fontWeightSemibold,
   },
})

type ControlledSniDropdownProps = {
   name: any
   control: Control<OrganisationWithUserAccountsDTO, any>
   label: string
   options: SNI[]
}

export const ControlledSniDropdown = ({ name, control, label, options }: ControlledSniDropdownProps) => {
   const classes = useStyles()

   const groupedByDepartment = useMemo(() => {
      const groups = options.reduce<Record<string, SNI[]>>((acc, sni) => {
         acc[sni.department] ??= []
         acc[sni.department].push(sni)
         return acc
      }, {})

      Object.values(groups).forEach((g) => g.sort((a, b) => a.code - b.code))

      return groups
   }, [options])

   const allIds = options.map((o) => o.id.toString())

   return (
      <Controller
         name={name}
         control={control}
         rules={{ required: t('RequiredField') }}
         render={({ field: { value = [], onChange }, fieldState: { error } }) => {
            const selectedIds = value.map((v: SNIOrganisationConnection) => v.sniId.toString())
            const selectedGroups = Object.entries(groupedByDepartment)
               .filter(([, snis]) => snis.every((sni) => selectedIds.includes(sni.id.toString())))
               .map(([department]) => department)

            const isAllSelected = selectedIds.length === options.length

            const setSelection = (ids: string[]) => {
               const mapped: SNIOrganisationConnection[] = ids
                  .map((id) => options.find((o) => o.id.toString() === id))
                  .filter((o): o is SNI => !!o)
                  .map((sni) => ({
                     sniId: sni.id,
                     sni,
                  }))

               onChange(mapped)
            }

            return (
               <Field label={label} required validationMessage={error?.message ?? null} validationState={error ? 'error' : null}>
                  <Dropdown
                     multiselect
                     selectedOptions={[...selectedIds, ...selectedGroups, ...(isAllSelected ? [t('All')] : [])]}
                     value={
                        selectedIds.length === options.length
                           ? t('All')
                           : value
                                .map((sniConn: SNIOrganisationConnection) => options.find((o) => o.id === sniConn.sniId))
                                .filter((sni: SNI): sni is SNI => !!sni)
                                .sort((a: SNI, b: SNI) => a.code - b.code)
                                .map((sni: SNI) => sni.code.toString().padStart(2, '0').padEnd(5, 'xxx'))
                                .join(', ')
                     }
                     onOptionSelect={(_, data) => setSelection(data.selectedOptions)}
                  >
                     {/* Select all */}
                     <OptionGroup>
                        <Option
                           className={classes.optionGroupHeader}
                           onClick={(e) => {
                              e.stopPropagation()
                              setSelection(selectedIds.length === options.length ? [] : allIds)
                           }}
                        >
                           {t('All')}
                        </Option>
                     </OptionGroup>

                     {/* Department groups */}
                     {Object.entries(groupedByDepartment).map(([department, snis]) => {
                        const departmentIds = snis.map((s) => s.id.toString())
                        const isGroupFullySelected = departmentIds.every((id) => selectedIds.includes(id))

                        return (
                           <OptionGroup key={department}>
                              <Option
                                 className={classes.optionGroupHeader}
                                 onClick={(e) => {
                                    e.stopPropagation()
                                    const next = isGroupFullySelected
                                       ? selectedIds.filter((id: string) => !departmentIds.includes(id))
                                       : Array.from(new Set([...selectedIds, ...departmentIds]))

                                    setSelection(next)
                                 }}
                              >
                                 {department}
                              </Option>

                              {snis.map((sni) => (
                                 <Option key={sni.id} value={sni.id.toString()} onClick={(e) => e.stopPropagation()}>
                                    {`${sni.code.toString().padStart(2, '0')} ${sni.comment}`}
                                 </Option>
                              ))}
                           </OptionGroup>
                        )
                     })}
                  </Dropdown>
               </Field>
            )
         }}
      />
   )
}
